import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const MerchantBranches = () => {
  const { userProfile, session } = useAuth();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [newBranch, setNewBranch] = useState({
    branch_name: '',
    branch_code: '',
    address: '',
    city: '',
    phone: '',
    email: '',
    manager_name: '',
    status: 'active'
  });

  useEffect(() => {
    fetchBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBranches = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = userProfile?.token || session?.access_token;
      if (!token) {
        setError('No authentication token available');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/merchant/branches`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setBranches(data.data || []);
      } else {
        setError(data.error || 'Failed to fetch branches');
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      setError('Failed to fetch branches');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError(null);

      const token = userProfile?.token || session?.access_token;
      if (!token) {
        setError('No authentication token available');
        return;
      }

      const url = editingBranch 
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/merchant/branches/${editingBranch.id}`
        : `${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/merchant/branches`;
      
      const method = editingBranch ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newBranch)
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        setShowAddModal(false);
        setNewBranch({
          branch_name: '',
          branch_code: '',
          address: '',
          city: '',
          phone: '',
          email: '',
          manager_name: '',
          status: 'active'
        });
        setEditingBranch(null);
        fetchBranches();
      } else {
        setError(data.error || 'Failed to save branch');
      }
    } catch (error) {
      console.error('Error saving branch:', error);
      setError('Failed to save branch');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (branchId) => {
    if (!window.confirm('Are you sure you want to delete this branch?')) return;
    
    try {
      const token = userProfile?.token || session?.access_token;
      if (!token) {
        setError('No authentication token available');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/merchant/branches/${branchId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        fetchBranches();
      } else {
        setError(data.error || 'Failed to delete branch');
      }
    } catch (error) {
      console.error('Error deleting branch:', error);
      setError('Failed to delete branch');
    }
  };

  const openEditModal = (branch) => {
    setEditingBranch(branch);
    setNewBranch({
      branch_name: branch.branch_name || '',
      branch_code: branch.branch_code || '',
      address: branch.address || '',
      city: branch.city || '',
      phone: branch.phone || '',
      email: branch.email || '',
      manager_name: branch.manager_name || '',
      status: branch.status || 'active'
    });
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingBranch(null);
    setNewBranch({
      branch_name: '',
      branch_code: '',
      address: '',
      city: '',
      phone: '',
      email: '',
      manager_name: '',
      status: 'active'
    });
  };

  const statusVariant = (status) => {
    if (status === 'active') return 'success';
    if (status === 'inactive') return 'destructive';
    if (status === 'suspended') return 'warning';
    return 'secondary';
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-xl text-muted-foreground">Loading branches...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Branch Management 🏢"
        description="Manage your business branches and locations"
        actions={
          <Button onClick={() => setShowAddModal(true)}>+ Add New Branch</Button>
        }
      />

      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="flex items-center justify-between pt-6 text-destructive">
            <span>{error}</span>
            <Button variant="ghost" size="sm" onClick={() => setError(null)}>✕</Button>
          </CardContent>
        </Card>
      )}

      {branches.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="mb-4 text-6xl">🏢</div>
            <h3 className="mb-2 text-xl font-semibold text-foreground">No branches yet</h3>
            <p className="mb-4 text-muted-foreground">Start by adding your first business branch</p>
            <Button onClick={() => setShowAddModal(true)}>Add Your First Branch</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {branches.map(branch => (
            <Card key={branch.id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl">🏢</div>
                    <div>
                      <CardTitle className="text-lg">{branch.branch_name}</CardTitle>
                      {branch.branch_code && (
                        <CardDescription>Code: {branch.branch_code}</CardDescription>
                      )}
                    </div>
                  </div>
                  <Badge variant={statusVariant(branch.status)}>{branch.status}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                {branch.address && <div><span className="font-medium text-foreground">📍 Address:</span> {branch.address}</div>}
                {branch.city && <div><span className="font-medium text-foreground">🏙️ City:</span> {branch.city}</div>}
                {branch.manager_name && <div><span className="font-medium text-foreground">👤 Manager:</span> {branch.manager_name}</div>}
                {branch.phone && <div><span className="font-medium text-foreground">📞 Phone:</span> {branch.phone}</div>}
                {branch.email && <div><span className="font-medium text-foreground">📧 Email:</span> {branch.email}</div>}
              </CardContent>
              <CardFooter className="gap-2">
                <Button className="flex-1" onClick={() => openEditModal(branch)}>Edit</Button>
                <Button variant="destructive" onClick={() => handleDelete(branch.id)}>Delete</Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showAddModal} onOpenChange={(open) => { if (!open) closeModal(); }}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBranch ? 'Edit Branch' : 'Add New Branch'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="branch_name">Branch Name *</Label>
                <Input
                  id="branch_name"
                  value={newBranch.branch_name}
                  onChange={(e) => setNewBranch({...newBranch, branch_name: e.target.value})}
                  placeholder="e.g., Downtown Branch"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="branch_code">Branch Code</Label>
                <Input
                  id="branch_code"
                  value={newBranch.branch_code}
                  onChange={(e) => setNewBranch({...newBranch, branch_code: e.target.value})}
                  placeholder="e.g., DT001"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Textarea
                id="address"
                value={newBranch.address}
                onChange={(e) => setNewBranch({...newBranch, address: e.target.value})}
                placeholder="Enter full address"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={newBranch.city}
                  onChange={(e) => setNewBranch({...newBranch, city: e.target.value})}
                  placeholder="e.g., Riyadh"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={newBranch.phone}
                  onChange={(e) => setNewBranch({...newBranch, phone: e.target.value})}
                  placeholder="e.g., +966501234567"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newBranch.email}
                  onChange={(e) => setNewBranch({...newBranch, email: e.target.value})}
                  placeholder="e.g., downtown@merchant.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="manager_name">Manager Name</Label>
                <Input
                  id="manager_name"
                  value={newBranch.manager_name}
                  onChange={(e) => setNewBranch({...newBranch, manager_name: e.target.value})}
                  placeholder="e.g., John Doe"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={newBranch.status}
                onValueChange={(value) => setNewBranch({...newBranch, status: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={closeModal} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1">
                {submitting ? 'Saving...' : (editingBranch ? 'Update Branch' : 'Add Branch')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MerchantBranches;
